import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Attaches the Clerk session token so the backend can verify who's asking.
// window.Clerk is the loaded Clerk instance ClerkProvider exposes globally -
// this lets a plain axios instance read the current session without needing
// to be a React hook itself. Requests proceed unauthenticated if there's no
// session; endpoints that require one reject with 401 on their own.
api.interceptors.request.use(async (config) => {
  try {
    const token = await window.Clerk?.session?.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // Not signed in, or Clerk hasn't finished loading yet - proceed as is.
  }
  return config;
});

// Returns { job_id, status, stages } - the sweep now runs in the background.
export async function initiateSweep(your_domain, competitor_domain, email, accept_terms = false) {
  const { data } = await api.post("/sweep/initiate", {
    your_domain, competitor_domain, email: email || null, accept_terms,
  });
  return data;
}

export async function getSweepJob(jobId) {
  const { data } = await api.get(`/sweep/job/${jobId}`);
  return data;
}

export async function getSweep(id) {
  const { data } = await api.get(`/sweep/${id}`);
  return data;
}

export async function createCheckout(package_id, report_id, email, client_country) {
  const origin_url = window.location.origin;
  const { data } = await api.post("/checkout/session", {
    package_id,
    origin_url,
    report_id: report_id || null,
    email: email || null,
    // Declared before the charge. The backend refuses a market-bound package
    // without a supported country - see backend/server.py's checkout gate.
    // Deliberately NOT written into Stripe metadata: the authoritative country
    // is still the one given at intake by the person filling in the P&L.
    client_country: client_country || null,
  });
  return data;
}

export async function getCheckoutStatus(sessionId) {
  const { data } = await api.get(`/checkout/status/${sessionId}`);
  return data;
}

// ---------- Account dashboard ----------
export async function getAccountMe() {
  const { data } = await api.get("/account/me");
  return data;
}

// ---------- Admin ----------
// The admin surface is gated by a shared secret rather than a login. The token
// is held in sessionStorage so it does not survive the tab closing.
export function setAdminToken(t) { sessionStorage.setItem("md_admin_token", t || ""); }
export function getAdminToken() { return sessionStorage.getItem("md_admin_token") || ""; }
export function clearAdminToken() { sessionStorage.removeItem("md_admin_token"); }
const adminHeaders = () => ({ "X-Admin-Token": getAdminToken() });
async function apiGetAdmin(path) {
  return api.get(path, { headers: adminHeaders() });
}

// ---------- Fulfilment ----------
export async function createEnquiry(payload) {
  const { data } = await api.post("/enquiry", payload);
  return data;
}

export async function submitAuditIntake(payload) {
  const { data } = await api.post("/audit-intake", payload);
  return data;
}

// The Shopify app's hand-off: binds an audit bought in the app to this account.
export async function redeemAuditClaim(token) {
  const { data } = await api.post(`/audit-claim/${encodeURIComponent(token)}`);
  return data;
}

export async function getIntakeStatus(sessionId) {
  const { data } = await api.get(`/audit-intake/${sessionId}`);
  return data;
}

export async function extractIntakeFinancials(file, sessionId) {
  const formData = new FormData();
  formData.append("file", file);
  // Proof of purchase. This endpoint sends the upload to the model, so it is
  // bound to a paid checkout rather than left open to the internet.
  formData.append("session_id", sessionId);
  const { data } = await api.post("/audit-intake/extract-financials", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// Parse ONE evidence-pack export (CSV/XLSX/PDF) deterministically and return
// minimal normalised aggregates for confirmation. Raw rows are never returned.
export async function parseEvidencePack(file, { source, platform, channel }, sessionId) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("session_id", sessionId);
  formData.append("source", source);
  if (platform) formData.append("platform", platform);
  if (channel) formData.append("channel", channel);
  const { data } = await api.post("/audit-intake/evidence-pack/parse", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function extractShippingPolicy(url, sessionId) {
  const { data } = await api.post("/audit-intake/extract-shipping-policy", { url, session_id: sessionId });
  return data;
}

export async function getBenchmarkCategories() {
  const { data } = await api.get("/benchmark-categories");
  return data.categories || {};
}

// ---------- Margin Audit report ----------
export async function getAuditJob(jobId) {
  const { data } = await api.get(`/audit/job/${jobId}`);
  return data;
}

export async function getAuditReport(reportId) {
  const { data } = await api.get(`/audit/report/${reportId}`);
  return data;
}

export async function getAuditReportByIntake(intakeId) {
  const { data } = await api.get(`/audit/report/by-intake/${intakeId}`);
  return data;
}

export async function getScenarioLevers() {
  const { data } = await api.get("/audit/scenario-levers");
  return data;
}

export async function runAuditScenario(reportId, lever, new_value) {
  const { data } = await api.post(`/audit/report/${reportId}/scenario`, { lever, new_value });
  return data;
}

// Comparator discovery and the measured segment map. Both are admin-only and
// operator-triggered: discovery proposes brands to READ and never a figure, and
// every price on the map is measured from a store's own catalogue afterwards.
export async function adminDiscoverComparators(payload) {
  const { data } = await api.post("/admin/segments/discover", payload, { headers: adminHeaders() });
  return data;
}
export async function adminBuildSegmentMap(payload) {
  const { data } = await api.post("/admin/segments/map", payload, { headers: adminHeaders() });
  return data;
}
export async function adminStats() {
  const { data } = await apiGetAdmin("/admin/stats")
  return data;
}
export async function adminCosts(days = 30) {
  const { data } = await apiGetAdmin(`/admin/costs?days=${days}`)
  return data;
}
export async function adminSaveCostSettings(payload) {
  const { data } = await api.put("/admin/costs/settings", payload, { headers: adminHeaders() });
  return data;
}
export async function adminTransactions() {
  const { data } = await apiGetAdmin("/admin/transactions")
  return data.items || [];
}
export async function adminEnquiries() {
  const { data } = await apiGetAdmin("/admin/enquiries")
  return data.items || [];
}
export async function adminIntakes() {
  const { data } = await apiGetAdmin("/admin/intakes")
  return data.items || [];
}
export async function adminSweeps() {
  const { data } = await apiGetAdmin("/admin/sweeps")
  return data.items || [];
}
export async function adminDownloadAuditReportPdf(reportId, filename) {
  const res = await api.get(`/admin/audit-report/${reportId}/pdf`, {
    headers: adminHeaders(),
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "margin-audit.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
// The briefing PDF, fetched rather than navigated to.
//
// This used to be window.open(`${API}/sweep/${id}/pdf`). A new tab is a plain
// browser navigation: it carries no Authorization header, and the token lives in
// one because the API is on a different origin to the app. So the endpoint -
// which requires an account and an accepted terms record, correctly - answered
// {"detail":"Not signed in"} and the buyer got a JSON error where their briefing
// should have been.
//
// Same blob pattern as adminDownloadAuditReportPdf above: axios carries the
// session token through the interceptor, and the anchor click hands the finished
// bytes to the browser. Any endpoint behind auth has to be reached this way.
export async function downloadSweepPdf(reportId, filename) {
  const res = await api.get(`/sweep/${reportId}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "margin-briefing.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function adminAcceptances(userId) {
  const { data } = await apiGetAdmin(
    `/admin/acceptances${userId ? `?user_id=${encodeURIComponent(userId)}` : ""}`);
  return data;
}

// Evidence export - a client's full acceptance history as CSV.
export async function adminExportAcceptances(userId) {
  const res = await api.get(
    `/admin/acceptances/export${userId ? `?user_id=${encodeURIComponent(userId)}` : ""}`,
    { headers: adminHeaders(), responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `margin-lens-acceptances-${userId || "all"}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function adminAuditReports() {
  const { data } = await apiGetAdmin("/admin/audit-reports")
  return data.items || [];
}
export async function adminReviewAuditReport(reportId, { operator_note, release }) {
  const { data } = await api.post(
    `/admin/audit-report/${reportId}/review`,
    { operator_note, release },
    { headers: adminHeaders() },
  );
  return data;
}


// ---------- Legal documents and acceptance ----------
//
// The server owns versions and content hashes. Nothing here sends them; the
// client names a context and the backend resolves what that means. A client
// able to assert which version it accepted could assert it without having.

export async function getLegalDocument(slug) {
  const { data } = await api.get(`/legal/document/${slug}`);
  return data;
}

export async function getLegalDocuments() {
  const { data } = await api.get("/legal/documents");
  return data.documents;
}

// { context, satisfied, documents[] } - documents carry full bodies so the
// consent surface renders the text it is recording acceptance of.
export async function getRequiredConsents(context) {
  const { data } = await api.get("/legal/required", { params: { context } });
  return data;
}

export async function acceptConsents(context, subject_ref = null) {
  const { data } = await api.post("/legal/accept", { context, subject_ref });
  return data;
}

// True when the server refused an action pending acceptance (HTTP 428).
export function isAcceptanceRequired(error) {
  return error?.response?.status === 428
    && error?.response?.data?.detail?.error === "acceptance_required";
}


// ---------- Self-serve privacy rights ----------
// Each is scoped server-side to the signed-in user; none takes an id, so no
// caller can name another account.

export async function downloadMyData() {
  const res = await api.get("/account/export", { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `margin-lens-my-data-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export async function getMyAcceptances() {
  const { data } = await api.get("/account/acceptances");
  return data.acceptances;
}

export async function deleteMyAccount(confirm) {
  const { data } = await api.post("/account/delete", { confirm });
  return data;
}
